import { useMemo, useState } from "react";
import { compareTexts, DEFAULT_THRESHOLD, THRESHOLDS, type Token } from "./similarityCore";
import { readTextFile, FILE_ACCEPT } from "./similarityFiles";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Compares two texts you provide against each other and highlights overlapping phrases with a match percentage — a text comparison tool, not a web-search plagiarism checker, since it has no connection to the internet or any academic database.`,
  how: `Paste your text and the text you want to compare it against (or load a .txt, .docx or .pdf file into either box), and the overlapping phrases are highlighted in both texts, in matching colours, with a match percentage shown. You can change the shared phrase length from 4 to 8 words.`,
  tip: `Use this to compare your draft against a specific source you're citing, or two of your own drafts — it's not a substitute for your school's actual plagiarism-detection tool, which checks against a much broader database.`,
};

// Same colour = the same shared phrase in both texts. The match number is also in each highlight's tooltip, so colour is never the only cue.
const PALETTE = [
  "bg-amber-200 dark:bg-amber-900/60",
  "bg-sky-200 dark:bg-sky-900/60",
  "bg-emerald-200 dark:bg-emerald-900/60",
  "bg-rose-200 dark:bg-rose-900/60",
  "bg-violet-200 dark:bg-violet-900/60",
  "bg-orange-200 dark:bg-orange-900/60",
];

function Highlighted({ tokens, groups, testId, label }: { tokens: Token[]; groups: Map<number, number>; testId: string; label: string }) {
  return (
    <div className="p-4 rounded-lg border bg-muted/20 leading-relaxed min-h-[80px]" data-testid={testId} role="region" aria-label={label}>
      {tokens.map((tok, i) => {
        const g = groups.get(i);
        return (
          <span key={i} data-match={g === undefined ? undefined : g + 1} title={g === undefined ? undefined : `Shared phrase ${g + 1}`} className={g === undefined ? "" : `${PALETTE[g % PALETTE.length]} rounded px-0.5`}>
            {tok.show}{tok.space ? " " : ""}
          </span>
        );
      })}
    </div>
  );
}

export default function TextSimilarityChecker() {
  const [textA, setTextA] = useState(
    "Climate change refers to long-term shifts in temperatures and weather patterns. These shifts may be natural, but since the 1800s, human activities have been the main driver of climate change."
  );
  const [textB, setTextB] = useState(
    "Climate change refers to long-term shifts in temperatures and weather patterns, mainly caused by human activities like burning fossil fuels since the 1800s."
  );
  const [threshold, setThreshold] = useState<number>(DEFAULT_THRESHOLD);
  const [fileError, setFileError] = useState<{ a: string; b: string }>({ a: "", b: "" });

  const result = useMemo(() => compareTexts(textA, textB, threshold), [textA, textB, threshold]);

  const { blocked } = useLogToolUsage(true, 3000);

  const loadFile = async (side: "a" | "b", file: File | undefined) => {
    if (!file) return;
    setFileError((e) => ({ ...e, [side]: "" }));
    try {
      const text = await readTextFile(file);
      (side === "a" ? setTextA : setTextB)(text);
    } catch (err) {
      setFileError((e) => ({ ...e, [side]: err instanceof Error ? err.message : "That file couldn't be read." }));
    }
  };
  const FileButton = ({ side, label }: { side: "a" | "b"; label: string }) => (
    <div className="mt-1">
      <Label htmlFor={`sim-file-${side}`} className="text-xs text-muted-foreground">{label}</Label>
      <input id={`sim-file-${side}`} type="file" accept={FILE_ACCEPT} className="block text-xs mt-0.5" onChange={e => { void loadFile(side, e.target.files?.[0]); e.target.value = ""; }} />
      {fileError[side] && <p role="alert" className="text-xs text-destructive mt-1">{fileError[side]}</p>}
    </div>
  );

  const canShow = !result.empty && !result.tooShort;

  return (
    <ToolContainer>
      <NoticeBox className="p-4 mb-6">
        <div className="flex gap-2">
          <AlertTriangle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm">
            This compares the two texts you paste below against{" "}
            <strong>each other only</strong> — it has no connection to the
            internet or any academic database, so it cannot tell you whether
            your writing matches something published elsewhere. Use it to
            compare your draft against a specific source you're citing, or two
            of your own drafts — not as a substitute for your school's actual
            plagiarism-detection tool.
          </p>
        </div>
      </NoticeBox>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label htmlFor="sim-a" className="mb-2 block text-sm">Your Text</Label>
          <Textarea
            id="sim-a"
            value={textA}
            onChange={e => setTextA(e.target.value)}
            className="min-h-[200px]"
          />
          <FileButton side="a" label="Or load a .txt, .docx or .pdf file into Your Text" />
        </div>
        <div>
          <Label htmlFor="sim-b" className="mb-2 block text-sm">Compare Against</Label>
          <Textarea
            id="sim-b"
            value={textB}
            onChange={e => setTextB(e.target.value)}
            className="min-h-[200px]"
          />
          <FileButton side="b" label="Or load a .txt, .docx or .pdf file into Compare Against" />
        </div>
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Label htmlFor="sim-threshold" className="text-sm">Shared phrase length</Label>
        <select id="sim-threshold" value={threshold} onChange={e => setThreshold(Number(e.target.value))} className="border rounded px-2 py-1 bg-background text-sm">
          {THRESHOLDS.map(n => <option key={n} value={n}>{n} words{n === DEFAULT_THRESHOLD ? " (default)" : ""}</option>)}
        </select>
        <span className="text-xs text-muted-foreground">Shorter finds more, looser matches.</span>
      </div>

      {blocked ? (
        <p className="text-sm text-center text-muted-foreground py-6 border rounded-lg">— blocked at today's usage limit —</p>
      ) : (
        <>
          <Card className="p-6 text-center mb-4">
            {result.empty ? (
              <p className="text-sm text-muted-foreground" role="status">Paste text into both boxes to compare them.</p>
            ) : result.tooShort ? (
              <p className="text-sm text-muted-foreground" role="status">Too short to compare. Each text needs at least 3 words.</p>
            ) : (
              <>
                <p className="text-3xl font-bold text-primary" data-testid="sim-percent">
                  {result.percentA.toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  of your text shares {result.size}+ word phrases with the comparison text
                  {" "}({result.percentB.toFixed(1)}% of the comparison text shares them with yours; {result.matchCount} shared phrase{result.matchCount === 1 ? "" : "s"})
                </p>
                {result.size < threshold && (
                  <p className="text-xs text-muted-foreground mt-1">One of the texts has under {threshold} words, so shorter phrases ({result.size} words) were compared.</p>
                )}
              </>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="mb-2 block text-sm">Your Text, With Matches Highlighted</Label>
              {canShow ? <Highlighted tokens={result.tokensA} groups={result.groupsA} testId="sim-pane-a" label="Your text with matches highlighted" />
                : <p className="text-sm text-muted-foreground p-4 rounded-lg border" data-testid="sim-pane-a-empty">Highlights appear here once both boxes have text.</p>}
            </div>
            <div>
              <Label className="mb-2 block text-sm">Comparison Text, With Matches Highlighted</Label>
              {canShow ? <Highlighted tokens={result.tokensB} groups={result.groupsB} testId="sim-pane-b" label="Comparison text with matches highlighted" />
                : <p className="text-sm text-muted-foreground p-4 rounded-lg border" data-testid="sim-pane-b-empty">Highlights appear here once both boxes have text.</p>}
            </div>
          </div>
          {canShow && result.groupsA.size > 0 && <p className="text-xs text-muted-foreground mt-2">The same colour marks the same shared phrase in both texts. Hover a highlight to see its number.</p>}
        </>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}

// Kept so existing imports (and tests) of the pure helpers from this file keep working.
export { tokenize, effectiveShingleSize, findMatchingSpans } from "./similarityCore";
